import { useState, useEffect, useCallback } from 'react';
import {
  fetchComparisonData,
  isPolygonConfigured,
} from '@/lib/polygon-api';
import {
  generateMockComparisonData,
  generateForecastData,
  ComparisonData,
} from '@/lib/mock-market-data';

export interface UseMarketDataOptions {
  selectedFund: string;
  years: number;
  portfolioValue: number;
  portfolioExpectedReturn?: number; // Actual portfolio expected return
  portfolioVolatility?: number; // Actual portfolio volatility
  mode: 'historical' | 'forecast';
  stressLevel?: number;
}

export interface UseMarketDataResult {
  data: ComparisonData | null;
  isLoading: boolean;
  error: string | null;
  isRealData: boolean;
  refetch: () => void;
}

/**
 * Hook to fetch market data from Polygon API with fallback to mock data
 */
export function useMarketData({
  selectedFund,
  years,
  portfolioValue,
  portfolioExpectedReturn = 0.07,
  portfolioVolatility = 0.15,
  mode,
  stressLevel = 0,
}: UseMarketDataOptions): UseMarketDataResult {
  const [data, setData] = useState<ComparisonData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRealData, setIsRealData] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    // For forecast mode, always use mock/simulated data
    if (mode === 'forecast') {
      const scenario = stressLevel <= -25 ? 'bear' : stressLevel <= -10 ? 'base' : 'bull';
      const forecastData = generateForecastData(years, portfolioValue, scenario, stressLevel, portfolioExpectedReturn, portfolioVolatility);
      setData(forecastData);
      setIsRealData(false);
      setIsLoading(false);
      return;
    }

    // For historical mode, try to fetch real data
    if (!isPolygonConfigured()) {
      console.log('Polygon API not configured, using mock data');
      const mockData = generateMockComparisonData(years, portfolioValue, selectedFund, portfolioExpectedReturn, portfolioVolatility);
      setData(mockData);
      setIsRealData(false);
      setIsLoading(false);
      return;
    }

    try {
      // Map fund selection to actual ticker
      const tickerMap: Record<string, string> = {
        'TACTICAL': 'TACTICAL', // Special case - simulated
        'VTI': 'VTI',
        'VOO': 'VOO',
        'VXUS': 'VXUS',
        'VGT': 'VGT',
        'IVV': 'IVV',
        'IEFA': 'IEFA',
        'AGG': 'AGG',
        'FXAIX': 'FXAIX',
        'FSKAX': 'FSKAX',
      };

      const ticker = tickerMap[selectedFund] || selectedFund;
      const realData = await fetchComparisonData(ticker, years, portfolioValue);

      // Convert to ComparisonData format
      const comparisonData: ComparisonData = {
        userPortfolio: realData.userPortfolio,
        benchmark: realData.benchmark,
        tacticalFund: realData.tacticalFund,
      };

      setData(comparisonData);
      setIsRealData(true);
      setError(null);
    } catch (err) {
      console.error('Error fetching real market data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch market data');

      // Fall back to mock data
      const mockData = generateMockComparisonData(years, portfolioValue, selectedFund, portfolioExpectedReturn, portfolioVolatility);
      setData(mockData);
      setIsRealData(false);
    } finally {
      setIsLoading(false);
    }
  }, [selectedFund, years, portfolioValue, portfolioExpectedReturn, portfolioVolatility, mode, stressLevel]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    isLoading,
    error,
    isRealData,
    refetch: fetchData,
  };
}
